/* mw-marquee — endless horizontal marquee for blocks/marquee.liquid (docs/17 §5). Light DOM.

   Markup (rendered by the block):
   <mw-marquee class="blk marquee …" data-speed-mode="rate|duration" data-rate="98" data-speed="30"
               data-direction="left|right" [data-hover-pause] [data-scroll]>
     <div class="marquee__viewport">
       <div class="marquee__track">
         <div class="marquee__group">…nested blocks…</div>
       </div>
     </div>
     [<template data-marquee-separator>…</template>]
     <button class="marquee__play" data-marquee-play data-label-pause="…" data-label-play="…" hidden>…</button>
   </mw-marquee>

   What the script adds (the server markup is a static row that scrolls sideways without it):
   - Separators from the template between the items (and after the last, so the loop is even);
     a data-cycle template hands its separators out in turn.
   - Copies of the group until the track is one group wider than the viewport. Copies are
     aria-hidden and inert, lose ids and editor attributes: screen readers and the keyboard meet
     the items once.
   - --marquee-shift (one group + gap, px) and --marquee-duration; marquee.css animates the track
     by exactly one group, so the loop is seamless. In «rate» mode the duration is derived from
     the measured shift (shift ÷ data-rate px/s), so the row runs at the same speed at every
     width — Helix runs 96–98 px/s at both 390 and 1440. In «duration» mode data-speed is the
     seconds one group takes, and a narrow screen therefore runs slower.
   - Pauses: the pause button (WCAG 2.2.2), hover (data-hover-pause, mouse), keyboard focus inside,
     a touch held on it, off screen, a hidden browser tab, a block selected in the theme editor.
   - data-scroll: page scroll nudges the loop, forward ×4 and backward ×8 (ms per px, as Helix);
     pointer devices only, and never while the shopper has paused it.
   - Reduced motion: no copies, no animation; the row scrolls sideways (marquee.css). */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const SCROLL_FORWARD = 4;
const SCROLL_BACK = 8;
const SCROLL_CLAMP = 50;

class MwMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector(':scope > .marquee__viewport');
    this.track = this.viewport?.querySelector(':scope > .marquee__track');
    this.group = this.track?.querySelector(':scope > .marquee__group');
    if (!this.group) return;

    this.abort = new AbortController();
    const { signal } = this.abort;
    this.pauses = new Set();
    this.playButton = this.querySelector(':scope > [data-marquee-play]');

    this.addSeparators();
    this.update();

    this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.group);
    reducedMotion.addEventListener('change', () => this.update(), { signal });

    this.playButton?.addEventListener(
      'click',
      () => (this.pauses.has('user') ? this.resume('user') : this.pause('user')),
      { signal }
    );

    if (this.hasAttribute('data-hover-pause')) {
      this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.pause('hover'), { signal });
      this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.resume('hover'), { signal });
    }
    this.addEventListener('pointerdown', (e) => e.pointerType !== 'mouse' && this.pause('touch'), { signal });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) =>
      this.addEventListener(type, (e) => e.pointerType !== 'mouse' && this.resume('touch'), { signal })
    );

    // Keyboard focus on an item (a link, a button) holds the row still; the pause button does not.
    this.addEventListener(
      'focusin',
      (event) => {
        const el = event.target;
        if (el !== this.playButton && el instanceof Element && el.matches(':focus-visible')) this.pause('focus');
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.group.contains(event.relatedTarget)) this.resume('focus');
      },
      { signal }
    );

    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause('hidden') : this.resume('hidden')), {
      signal,
    });

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')),
        { rootMargin: '100px 0px' }
      );
      this.visibilityObserver.observe(this);
    }

    if (this.hasAttribute('data-scroll')) {
      this.lastScrollY = window.scrollY;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    }

    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (e) => e.target instanceof Element && this.contains(e.target) && this.pause('editor'),
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        (e) => e.target instanceof Element && this.contains(e.target) && this.resume('editor'),
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
    cancelAnimationFrame(this.scrollFrame);
  }

  /* ---------- Layout ---------- */

  /* A template marked data-cycle holds several separators (product colours); item n gets
     separator n modulo their number, so the colours follow one another along the row. */
  addSeparators() {
    const template = this.querySelector(':scope > template[data-marquee-separator]');
    if (!template) return;
    const cycle = template.hasAttribute('data-cycle') ? [...template.content.children] : null;
    if (cycle && !cycle.length) return;
    let n = 0;
    [...this.group.children].forEach((item) => {
      if (item.classList.contains('marquee__sep')) return;
      const next = item.nextElementSibling;
      if (next?.classList.contains('marquee__sep')) return;
      item.after(cycle ? cycle[n % cycle.length].cloneNode(true) : template.content.cloneNode(true));
      n += 1;
    });
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.update());
  }

  clones() {
    return [...this.track.children].filter((el) => el !== this.group);
  }

  update() {
    const still = reducedMotion.matches;
    const groupWidth = this.group.getBoundingClientRect().width;
    const viewportWidth = this.viewport.clientWidth;

    if (still || !groupWidth || !viewportWidth) {
      this.clones().forEach((el) => el.remove());
      this.classList.remove('is-running');
      this.classList.toggle('is-static', still);
      if (this.playButton) this.playButton.hidden = true;
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = groupWidth + gap;
    // Enough copies that the viewport is always covered while the track moves by one step.
    const wanted = Math.max(1, Math.ceil(viewportWidth / step));
    const clones = this.clones();
    for (let i = clones.length; i < wanted; i += 1) this.track.append(this.makeClone());
    clones.slice(wanted).forEach((el) => el.remove());

    this.style.setProperty('--marquee-shift', `${step.toFixed(2)}px`);
    this.style.setProperty('--marquee-duration', `${this.duration(step).toFixed(3)}s`);
    this.classList.remove('is-static');
    this.classList.add('is-running');
    if (this.playButton) this.playButton.hidden = false;
    this.renderPause();
  }

  /* Seconds for one group to pass. «rate» keeps a constant px/s whatever the track measures;
     «duration» is the seconds-per-loop setting. Clamped so a one-item row cannot ask the
     browser for a near-zero duration. */
  duration(step) {
    if (this.dataset.speedMode === 'duration') {
      return Math.max(1, Number.parseFloat(this.dataset.speed) || 30);
    }
    const rate = Math.max(1, Number.parseFloat(this.dataset.rate) || 98);
    return Math.max(0.5, step / rate);
  }

  makeClone() {
    const clone = this.group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.classList.add('marquee__group--clone');
    [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach((el) => {
      el.removeAttribute('id');
      el.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    return clone;
  }

  /* ---------- Pausing ---------- */

  pause(reason) {
    this.pauses.add(reason);
    this.renderPause();
  }

  resume(reason) {
    this.pauses.delete(reason);
    this.renderPause();
  }

  renderPause() {
    this.classList.toggle('is-paused', this.pauses.size > 0);
    const button = this.playButton;
    if (!button) return;
    const paused = this.pauses.has('user');
    button.toggleAttribute('data-paused', paused);
    const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  }

  /* ---------- Scroll coupling ---------- */

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      const y = window.scrollY;
      const delta = Math.max(-SCROLL_CLAMP, Math.min(SCROLL_CLAMP, y - this.lastScrollY));
      this.lastScrollY = y;
      if (!delta || !finePointer.matches || this.pauses.has('user') || this.pauses.has('offscreen')) return;
      if (!this.classList.contains('is-running')) return;
      const factor = delta < 0 ? SCROLL_BACK : SCROLL_FORWARD;
      this.track.getAnimations().forEach((animation) => {
        const duration = Number(animation.effect?.getComputedTiming().duration) || 0;
        if (!duration || animation.currentTime === null) return;
        const time = Number(animation.currentTime) + delta * factor;
        animation.currentTime = ((time % duration) + duration) % duration;
      });
    });
  }
}

if (!customElements.get('mw-marquee')) customElements.define('mw-marquee', MwMarquee);

export { MwMarquee };
